import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../styles/theme';
import { DataContext } from '../../../context/DataContext';
import { AuthContext } from '../../../context/AuthContext';
import { getLastActivityByCause } from '../../../firebase/services';
import { Button } from '../../common/Button';
import LightwayWordmark from '../../common/LightwayWordmark';
import { CauseCard } from './CauseCard';
import { AddCauseCard } from './AddCauseCard';
import { BottomNav } from '../../common/BottomNav';
import { FeedbackLink } from '../../common/FeedbackLink';
import NotificationPrompt from '../../common/NotificationPrompt';
import ActivityReminderBanner from '../../common/ActivityReminderBanner';

const HomeContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: ${theme.spacing.md};
  padding-bottom: 80px; /* Space for bottom navigation */
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: ${theme.spacing.lg};
  padding-top: ${theme.spacing.lg};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSizes.large};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.deepNavy};
`;

const SectionContainer = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const ViewAllLink = styled(Link)`
  font-size: ${theme.typography.fontSizes.small};
  color: blue;
  display: block;
  text-align: right;
  margin-top: ${theme.spacing.xs};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: ${theme.typography.fontSizes.large};
  color: ${theme.colors.deepNavy};
`;

const ErrorContainer = styled.div`
  background-color: #ffebee;
  color: #c62828;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.medium};
  margin-bottom: ${theme.spacing.md};
`;

const GetInspiredButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.white};
  color: ${theme.colors.deepNavy};
  border: 2px solid ${theme.colors.deepNavy};
  border-radius: ${theme.borderRadius.medium};
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.large};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.colors.deepNavy};
    color: ${theme.colors.white};
  }
`;

export const HomeScreen = () => {
  const { t } = useTranslation();
  const { causes, loading, error, lastUpdated } = useContext(DataContext);
  const { currentUser } = useContext(AuthContext);
  const [lastActivityDates, setLastActivityDates] = useState({});

  useEffect(() => {
    const fetchLastDates = async () => {
      if (!currentUser || causes.length === 0) return;
      const dates = {};
      await Promise.all(
        causes.map(async (cause) => {
          const timestamp = await getLastActivityByCause(currentUser.uid, cause.title);
          if (timestamp) dates[cause.title] = timestamp;
        })
      );
      setLastActivityDates(dates);
    };
    fetchLastDates();
  }, [currentUser, causes, lastUpdated]);

  if (loading) {
    return <LoadingContainer>{t('common.loading')}</LoadingContainer>;
  }

  return (
    <HomeContainer>
      <Header>
        <h1 className="lw-visually-hidden">Home</h1>
        <LightwayWordmark variant="compact" iconSrc="/logo192.png" />
      </Header>

      {error && (
        <ErrorContainer>
          {error}
        </ErrorContainer>
      )}

      <ButtonContainer>
        <Link to="/log-activity" style={{ flex: 1, textDecoration: 'none' }}>
          <Button primary>
            {t('home.logAnActivity')}
          </Button>
        </Link>
        <Link to="/progress" style={{ flex: 1, textDecoration: 'none' }}>
          <Button secondary>
            {t('home.viewProgress')}
          </Button>
        </Link>
      </ButtonContainer>

      <NotificationPrompt />
      <ActivityReminderBanner />

      <SectionContainer>
        <SectionTitle>{t('home.myCauses')}</SectionTitle>
        {causes.length > 0 ? (
          causes.map(cause => {
            return <CauseCard key={cause.docId} cause={cause} lastActivityDate={lastActivityDates[cause.title] || null} />;
          })
        ) : (
          <p>{error ? t('home.noCausesError') : t('home.noCauses')}</p>
        )}
        {causes.length < 3 && <AddCauseCard />}
        <ViewAllLink to="/causes">{t('home.viewAll')}</ViewAllLink>
      </SectionContainer>

      <Link to="/get-inspired" style={{ textDecoration: 'none', display: 'block', marginBottom: theme.spacing.lg }}>
        <GetInspiredButton>
          {t('home.getInspired')}
        </GetInspiredButton>
      </Link>

      <FeedbackLink />
      <BottomNav />
    </HomeContainer>
  );
};
